/* eslint-disable camelcase */
import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import environment from 'src/util/environment';
import {
  getIntercomBootGeneration,
  getIntercomPayload,
  getNextIntercomBootGeneration,
  renderIntercom,
  shutdownIntercom,
} from 'src/util/intercomSession';
import logger from 'src/util/logger';

// The Messenger session lives in intercomSession so the Relay network layer can
// shut it down without importing this module (which depends on that layer).
export { getIntercomPayload, renderIntercom, shutdownIntercom };

const intercomUserJwtQuery = graphql`
  query intercomUserJwtQuery {
    viewer {
      intercomUserJwt
    }
  }
`;

/**
 * Fetches the signed Intercom Messenger JWT for the signed-in user from Syndesi.
 * @returns {Promise<string|null>} - the JWT, or null when Syndesi has no Messenger
 *   secret configured or the request fails
 */
export const getIntercomUserJwt = () => fetchQuery(
  environment,
  intercomUserJwtQuery,
  {},
  { fetchPolicy: 'network-only' },
).toPromise()
  .then((data) => data?.viewer?.intercomUserJwt || null)
  .catch((error) => {
    logger.log({ message: 'Could not fetch the Intercom user JWT', error });
    return null;
  });

/**
 * Concatenates the first and last name of the user.
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} - username
 */
export const getUserName = (firstName, lastName) => `${firstName || ''} ${lastName || ''}`?.trim();

/**
 * Render the intercom widget at app start and after a successful login. A signed-in user's
 * Messenger is booted with their signed JWT when Syndesi provides one.
 * @returns {Promise<void>}
 */
export const intercomHandler = async () => {
  const { INTERCOM_APP_ID } = APIConfig();
  if (!INTERCOM_APP_ID) {
    logger.log('Could not load Intercom widget because app id is missing');
    return;
  }
  const generation = getNextIntercomBootGeneration();
  const userJwt = localStorage.getItem('loggedInUser') ? await getIntercomUserJwt() : null;
  if (generation !== getIntercomBootGeneration()) {
    return;
  }
  renderIntercom(INTERCOM_APP_ID, userJwt);
};

/**
 * Whether this deployment uses Intercom as its support channel. Deployments
 * that run their own support chat leave INTERCOM_APP_ID unset, so callers must
 * offer a non-Intercom route to support when this is false.
 * @returns {boolean} - true when Intercom is the configured support channel
 */
export const isIntercomEnabled = () => Boolean(APIConfig().INTERCOM_APP_ID);

/**
 * Open the Intercom messenger. No-op when the widget has not booted.
 * @returns {void}
 */
export const showIntercom = () => {
  if (typeof window.Intercom === 'function') {
    window.Intercom('show');
  }
};

/**
 * Set logged in user details in local storage.
 * Logged in User details may contain email, id, first and last name.
 * @param {object} userAttributes
 * @returns {void}
 */
export const setLoggedInUserDetails = (userAttributes) => {
  if (!userAttributes) {
    return;
  }
  const { email, family_name: lastName, given_name: firstName } = userAttributes;
  const loggedInUser = {
    email,
    name: getUserName(firstName, lastName),
    user_id: userAttributes['custom:id'],
  };
  localStorage.setItem('loggedInUser', JSON.stringify(loggedInUser));
  intercomHandler();
};
